import { useMemo } from "react";
import { useDashTheme } from "../hooks/useDashTheme";

/* Shared surface treatment for the Growth pages.
   ─────────────────────────────────────────────────────────────────────────
   ADDITIVE. Nothing here mutates design/tokens.js, so every existing screen
   keeps the surfaces it has today. Only the pages that opt in by calling
   useSurface() get this treatment.

   The base contrast problem it was written for has since been fixed at source
   (design/tokens.js, 2026-08-11: the light canvas was the same colour as its
   own inset wells and only 3% off a white card). These values are kept in step
   with those tokens.

   What remains here is what does NOT belong in global tokens: a gradient
   canvas, the ambient colour cast, and hue-keyed tints/washes that only the
   Growth pages use. */
export function useSurface() {
  const t = useDashTheme();

  return useMemo(() => {
    const dark = t.isDark;

    return {
      /* The page canvas. A soft vertical gradient rather than a flat fill, so
         the top of the page carries a little more weight than the bottom and
         the eye has somewhere to enter. */
      canvas: dark
        ? "linear-gradient(180deg, #191B1D 0%, #1D1F21 42%, #1D1F21 100%)"
        : "linear-gradient(180deg, #CFD7E3 0%, #D4DBE6 46%, #D6DDE7 100%)", // 2026-08-29: centred on the graphical-dashboard canvas #D4DBE6

      /* An ambient colour cast at the top of the page — the "intelligence"
         violet at very low opacity. Enough to tint the canvas, never enough to
         compete with the data. */
      ambient: dark
        ? "radial-gradient(1100px 320px at 18% -8%, rgba(168,139,255,0.13), transparent 62%), radial-gradient(900px 300px at 88% -12%, rgba(120,159,232,0.10), transparent 60%)"
        : "radial-gradient(1100px 320px at 18% -8%, rgba(109,74,255,0.09), transparent 62%), radial-gradient(900px 300px at 88% -12%, rgba(66,115,209,0.08), transparent 60%)",

      /* Cards keep the token surface but gain a border and shadow that are
         actually visible against the deeper canvas. */
      card: t.c.surface,
      cardBorder: dark ? "#3A3C3E" : "#DCE0E7",
      cardShadow: dark
        ? "0 1px 2px rgba(0,0,0,0.30), 0 8px 24px rgba(0,0,0,0.22)"
        : "0 1px 2px rgba(24,32,46,0.07), 0 8px 24px rgba(24,32,46,0.08)",
      cardShadowHover: dark
        ? "0 2px 4px rgba(0,0,0,0.34), 0 16px 40px rgba(0,0,0,0.30)"
        : "0 2px 6px rgba(24,32,46,0.10), 0 18px 44px rgba(24,32,46,0.13)",

      /* Inset wells inside a card, matching tokens.surfaceMuted. */
      inset: dark ? "#2A2C2E" : "#F1F3F7",
      insetStrong: dark ? "#303234" : "#E3E7EE",

      /* Hairlines that survive both themes. */
      hairline: dark ? "#3A3C3E" : "#DFE3EA",

      /** A tinted panel keyed to a hue — for callouts and highlighted regions. */
      tint: (hex, strength = 1) => (dark
        ? `${hex}${Math.round(18 * strength).toString(16).padStart(2, "0")}`
        : `${hex}${Math.round(13 * strength).toString(16).padStart(2, "0")}`),

      /** A soft directional wash for the top of a card, keyed to a hue. */
      wash: (hex) => (dark
        ? `linear-gradient(160deg, ${hex}18 0%, transparent 74%)`
        : `linear-gradient(160deg, ${hex}12 0%, transparent 74%)`),
    };
  }, [t.isDark, t.c.surface]);
}

/** Full-bleed canvas wrapper. PageShell paints t.c.bg on its own root, so a
 *  page that wants a different canvas has to bleed past the shell's padding
 *  rather than simply setting its own background. */
export function canvasStyle(sf) {
  return {
    position: "relative",
    background: sf.canvas,
    // Bleed horizontally past PageShell's 24px gutter, then restore it inside.
    margin: "0 -24px",
    padding: "20px 24px 40px",
    minHeight: "100%",
  };
}

/** The ambient glow layer. Rendered once, behind everything, pointer-inert. */
export function ambientStyle(sf) {
  return {
    position: "absolute", inset: 0, pointerEvents: "none",
    background: sf.ambient,
  };
}
